import CheckIcon from "@mui/icons-material/Check";
import { ListItemIcon, ListItemText, ListSubheader, MenuItem, Typography } from "@mui/material";
import * as React from "react";
import { sortHangars } from "../screens/Hangars/HangarsPresenter";
import { sortRamps } from "../screens/Ramps/RampsPresenter";
import { FBO, Hangar, Ramp } from "../types";

interface UseGroupedLocationMenuOptions {
  hangars: Hangar[];
  ramps: Ramp[];
  activeFBO: FBO | null;
  currentLocationId?: string | null;
  onLocationClick: (location: Hangar | Ramp) => void;
}

export function useGroupedLocationMenu({
  hangars,
  ramps,
  activeFBO,
  currentLocationId,
  onLocationClick,
}: UseGroupedLocationMenuOptions) {
  // Sort hangars and ramps using existing sort functions
  const sortedHangars = React.useMemo(
    () => hangars.sort((a, b) => sortHangars(a, b, activeFBO?.settings?.hangarDisplayOrder)),
    [hangars, activeFBO]
  );

  const sortedRamps = React.useMemo(
    () => ramps.sort((a, b) => sortRamps(a, b, activeFBO?.settings?.rampDisplayOrder)),
    [ramps, activeFBO]
  );

  // Render a single location menu item
  const renderLocationItem = React.useCallback(
    (location: Hangar | Ramp) => {
      const isCurrentLocation = location.id === currentLocationId;

      return (
        <MenuItem
          key={`location-${location.id}`}
          onClick={() => onLocationClick(location)}
        >
          {isCurrentLocation && (
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          )}
          <ListItemText inset={!isCurrentLocation}>
            <Typography variant="button">{location.name}</Typography>
          </ListItemText>
        </MenuItem>
      );
    },
    [currentLocationId, onLocationClick]
  );

  // Render all grouped menu items
  const renderMenuItems = React.useCallback(() => {
    const items: React.ReactNode[] = [];

    // Hangars section
    if (sortedHangars.length > 0) {
      items.push(
        <ListSubheader
          key="hangars-header"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            lineHeight: "32px",
            py: 0,
            mt: 0.5,
            mb: 0,
          }}
        >
          HANGARS
        </ListSubheader>
      );
      sortedHangars.forEach((hangar) => {
        items.push(renderLocationItem(hangar));
      });
    }

    // Ramps section
    if (sortedRamps.length > 0) {
      items.push(
        <ListSubheader
          key="ramps-header"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            lineHeight: "32px",
            py: 0,
            mt: 0.5,
            mb: 0,
          }}
        >
          RAMPS
        </ListSubheader>
      );
      sortedRamps.forEach((ramp) => {
        items.push(renderLocationItem(ramp));
      });
    }

    return items;
  }, [sortedHangars, sortedRamps, renderLocationItem]);

  return {
    renderMenuItems,
    sortedHangars,
    sortedRamps,
  };
}
